import type { ChartInstance } from "../kline-helpers";
import { DEFAULT_DRAWING_COLOR } from "@/lib/chart-drawings/defaults";
import type { DrawingPoint } from "@/lib/chart-drawings/types";

export interface Point2D {
  x: number;
  y: number;
}

export interface FibLevel {
  value: number;
  visible: boolean;
}

export interface RegressionResult {
  slope: number;
  intercept: number;
  stdDev: number;
  count: number;
}

const EXTENSION_FACTOR = 10_000;

export function extendLineThroughPoints(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): [Point2D, Point2D] {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length === 0) {
    return [
      { x: x1, y: y1 },
      { x: x2, y: y2 },
    ];
  }
  const dirX = dx / length;
  const dirY = dy / length;
  return [
    { x: x1 - dirX * EXTENSION_FACTOR, y: y1 - dirY * EXTENSION_FACTOR },
    { x: x2 + dirX * EXTENSION_FACTOR, y: y2 + dirY * EXTENSION_FACTOR },
  ];
}

/**
 * The x-range a drawing occupies — its own start/end on the time axis.
 * `extendRightTo` opts a study into projecting forward past its last anchor.
 */
export function spanX(
  coords: Point2D[],
  extendRightTo?: number
): { minX: number; maxX: number } {
  if (!coords.length) return { minX: 0, maxX: 0 };
  let minX = coords[0].x;
  let maxX = coords[0].x;
  for (const c of coords) {
    if (c.x < minX) minX = c.x;
    if (c.x > maxX) maxX = c.x;
  }
  if (typeof extendRightTo === "number" && extendRightTo > maxX) maxX = extendRightTo;
  return { minX, maxX };
}

/**
 * The segment of the line through (x1,y1)-(x2,y2) that lies within [xMin,xMax].
 *
 * This is the bounded counterpart to `extendLineThroughPoints`: Fibonacci studies
 * must start and stop on the time axis where the user anchored them, not run out
 * to EXTENSION_FACTOR in both directions.
 */
export function segmentBetween(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  xMin: number,
  xMax: number
): [Point2D, Point2D] {
  const dx = x2 - x1;
  if (dx === 0) {
    // Vertical line: clip nothing horizontally, keep the given endpoints.
    return [
      { x: x1, y: y1 },
      { x: x2, y: y2 },
    ];
  }
  const slope = (y2 - y1) / dx;
  const lo = Math.min(xMin, xMax);
  const hi = Math.max(xMin, xMax);
  return [
    { x: lo, y: y1 + (lo - x1) * slope },
    { x: hi, y: y1 + (hi - x1) * slope },
  ];
}

export function parallelLineThroughPoint(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  /** When given, the result is clipped to this x-range instead of extended. */
  bounds?: { minX: number; maxX: number }
): [Point2D, Point2D] {
  const vx = bx - ax;
  const vy = by - ay;
  const len = Math.sqrt(vx * vx + vy * vy);
  if (len === 0) {
    return [
      { x: cx, y: cy },
      { x: cx, y: cy },
    ];
  }
  const distance = ((cx - ax) * -vy + (cy - ay) * vx) / len;
  const dx = ax + (distance * -vy) / len;
  const dy = ay + (distance * vx) / len;
  const ex = bx + (distance * -vy) / len;
  const ey = by + (distance * vx) / len;
  if (bounds) return segmentBetween(dx, dy, ex, ey, bounds.minX, bounds.maxX);
  return extendLineThroughPoints(dx, dy, ex, ey);
}

export function lineAtAngleFromAnchor(
  anchor: Point2D,
  length: number,
  angleDeg: number
): [Point2D, Point2D] {
  const rad = (angleDeg * Math.PI) / 180;
  const x2 = anchor.x + length * Math.cos(rad);
  const y2 = anchor.y - length * Math.sin(rad);
  return extendLineThroughPoints(anchor.x, anchor.y, x2, y2);
}

export function visibleFibLevels(
  levels: FibLevel[] | undefined,
  fallback: FibLevel[]
): FibLevel[] {
  const src = levels?.length ? levels : fallback;
  return src.filter((l) => l.visible !== false);
}

export function fibPriceLevels(
  price0: number,
  price1: number,
  levels: FibLevel[]
): Array<{ level: number; price: number }> {
  const diff = price1 - price0;
  return levels.map((l) => ({
    level: l.value,
    price: price0 + diff * l.value,
  }));
}

export function fibTimeStamps(
  t0: number,
  t1: number,
  levels: FibLevel[],
  maxLines = 50
): number[] {
  const span = t1 - t0;
  if (span === 0) return [t0];
  const stamps: number[] = [];
  for (const l of levels) {
    if (stamps.length >= maxLines) break;
    stamps.push(t0 + span * l.value);
  }
  return stamps;
}

export function cycleTimeStamps(
  t0: number,
  t1: number,
  maxLines = 50
): number[] {
  const period = Math.abs(t1 - t0);
  if (period === 0) return [t0];
  const start = Math.min(t0, t1);
  const stamps: number[] = [];
  for (let k = 0; k < maxLines; k++) {
    stamps.push(start + k * period);
  }
  return stamps;
}

export function gannFanRayEndpoints(
  origin: Point2D,
  corner: Point2D,
  divisions: number
): Array<[Point2D, Point2D]> {
  const n = Math.max(2, Math.min(32, divisions));
  const rays: Array<[Point2D, Point2D]> = [];
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const end = {
      x: origin.x + (corner.x - origin.x) * t,
      y: origin.y + (corner.y - origin.y) * t,
    };
    rays.push(extendLineThroughPoints(origin.x, origin.y, end.x, end.y));
  }
  return rays;
}

export function linearRegressionBars(
  chart: ChartInstance | null | undefined,
  t0: number,
  t1: number
): RegressionResult | null {
  if (!chart || typeof chart.getDataList !== "function") return null;
  let dataList: Array<{ timestamp: number; close?: number }>;
  try {
    dataList = chart.getDataList() as Array<{ timestamp: number; close?: number }>;
  } catch {
    return null;
  }
  if (!dataList?.length) return null;

  const minT = Math.min(t0, t1);
  const maxT = Math.max(t0, t1);
  const bars = dataList.filter(
    (b) => b.timestamp >= minT && b.timestamp <= maxT && typeof b.close === "number"
  );
  if (bars.length < 2) return null;

  const n = bars.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  for (let i = 0; i < n; i++) {
    const x = bars[i].timestamp;
    const y = bars[i].close as number;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return null;

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  let sumSq = 0;
  for (let i = 0; i < n; i++) {
    const y = bars[i].close as number;
    const predicted = slope * bars[i].timestamp + intercept;
    const r = y - predicted;
    sumSq += r * r;
  }
  const stdDev = Math.sqrt(sumSq / n);

  return { slope, intercept, stdDev, count: n };
}

export function regressionYAt(
  reg: RegressionResult,
  timestamp: number
): number {
  return reg.slope * timestamp + reg.intercept;
}

export function computeChannelFromRegression(
  chart: ChartInstance,
  points: DrawingPoint[],
  deviations = 2
): Record<string, unknown> | null {
  if (points.length < 2) return null;
  const t0 = points[0].timestamp;
  const t1 = points[1].timestamp;
  const reg = linearRegressionBars(chart, t0, t1);
  if (!reg) return null;
  return {
    slope: reg.slope,
    intercept: reg.intercept,
    stdDev: reg.stdDev,
    deviations,
    tMin: Math.min(t0, t1),
    tMax: Math.max(t0, t1),
  };
}

export function defaultLineStyleFrom(
  style: Record<string, unknown> | undefined,
  colorKey = "color"
) {
  const lineStyle = (style?.lineStyle as string) || "solid";
  return {
    color: (style?.[colorKey] as string) || (style?.color as string) || DEFAULT_DRAWING_COLOR,
    lineWidth: (style?.lineWidth as number) || 1,
    lineStyle,
  };
}
